import { ChangeDetectionStrategy, Component, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FiTasksViewMode } from '@fi-shared/fi-task-shared/fi-task-enums';
import { ObjectTypes } from '@fi-shared/types';
import { IItemsListDataFetchResult, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ITaskParameter } from '@fi-shared/fi-task-shared/fi-task-shared';
import { ITaskV2 } from '../../task-v2/fi-task-v2';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiTaskV2Service } from '../../task-v2/fi-task-v2.service';
import { FiTaskV2HelperService } from '../../task-v2/fi-task-v2-helper.service';
import { FiTaskColumns } from '../../task-v2/fi-task-v2-enums';
import { takeUntil } from 'rxjs/operators';
import { Animations } from '@fi-shared/utils/animations';
import { FiTaskV2SyncService } from '../../task-v2/fi-task-v2-sync.service';
import { FiTaskSyncService } from '@fi-shared/fi-task-shared/fi-task-sync-service';
import { IItemsListSortOption } from '@fi-app/shared/fi-items-list/fi-items-list-sort-select/fi-items-list-sort-select.component';
import { IItemsListSort } from '@fi-shared/fi-items-list/fi-items-list.d';
import { FiTaskV2ListHelperService } from './fi-task-v2-list-helper.service';
import { DeepReadonly } from '@fi-app/shared/types/deep-readonly';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';

@Component({
    selector: 'fi-task-v2-list',
    templateUrl: './fi-task-v2-list.component.html',
    styleUrl: './fi-task-v2-list.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-v2-list fi-container' },
    animations: [Animations.fadeHeight()],
    standalone: false,
})
export class FiTaskV2ListComponent extends FiRemoteStateFullDataSource<ITaskV2> implements OnInit {
    @Input({ required: true }) viewMode: FiTasksViewMode;
    private readonly fiTaskV2HelperService = inject(FiTaskV2HelperService);
    private readonly fiTaskV2Service = inject(FiTaskV2Service);
    private readonly fiTaskV2SyncService = inject(FiTaskV2SyncService);
    private readonly fiTaskSyncService = inject(FiTaskSyncService);
    private readonly fiTaskV2ListHelperService = inject(FiTaskV2ListHelperService);
    // numberOfRecordsToTake = 1000; // for tests

    public readonly defaultSortColumnName = undefined;
    public readonly defaultSortDirection = undefined;
    public readonly itemsListType = FiItemsListType.TaskV2List;
    public readonly objectType = ObjectTypes.Task;

    protected sortOptions: IItemsListSortOption[] = [];
    protected readonly sideListOptions: DeepReadonly<ISideListOptionsItem[]> =
        this.fiTaskV2ListHelperService.sideListOptions;

    protected readonly FiTasksViewMode = FiTasksViewMode;

    public ngOnInit(): void {
        this.sortOptions = this.fiTaskV2ListHelperService.getSortOptions(this.viewMode);
        this.fiTaskV2SyncService.loadTasksCountForTabs(this.viewMode);
        this.subscribeToTaskStatusChange();
        this.subscribeToSyncTasks();
    }

    private subscribeToSyncTasks(): void {
        this.fiTaskSyncService.updatedTasksSyncResult$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((syncResult) => {
            this.fiTaskV2SyncService.loadTasksCountForTabs(this.viewMode).then(() => this.cdr.markForCheck());
        });
    }

    /*
    getTasksCountText(): string {
        let count: number;
        if (this.filterType === SideListOptionType.TasksAssignedToMe) {
            count = this.fiTaskV2SyncService.assignedToMeTasksCount;
        } else if (this.filterType === SideListOptionType.TasksPrivate) {
            count = this.fiTaskV2SyncService.privateTasksCount;
        } else if (this.filterType === SideListOptionType.TasksAssignedByMe) {
            count = this.fiTaskV2SyncService.assignedByMeTasksCount;
        }
        return this.appTextService.get('task.tasks_count') + ': ' + count + ',';
    }
    */

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<ITaskV2>> {
        return new Promise<IItemsListDataFetchResult<ITaskV2>>((resolve, reject) => {
            const parameter = this.getParameter(loadArgs);
            this.fiTaskV2Service.getTasksListData(parameter).then((data) => {
                resolve(data);
            }, reject);
        });
    }

    //# region sorting

    protected setSortData(): void {
        const sortColumn = this.fiTaskV2ListHelperService.viewModeToDefaultSortColumn[this.viewMode];
        if (sortColumn) {
            this.setDefaultSorting(sortColumn);
        } else {
            throw new Error(`unknown viewMode ${this.viewMode} in setSortData`);
        }
    }

    protected selectedSortChange($event: IItemsListSort) {
        this.sortColumn = $event.ColumnName;
        this.sortDirection = $event.SortDirection;
        super.onSortChanged(undefined); // todo, check if we don't need argument,remove later
    }

    private subscribeToTaskStatusChange(): void {
        this.fiTaskV2HelperService.taskStatusChange$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(({ task, newStatus }) => {
                this.onTaskStatusChange(task, newStatus);
            });
    }

    private getParameter(loadArgs: ILoadDataArgs): ITaskParameter {
        const parameter = super.getItemsListParameter(loadArgs) as ITaskParameter;
        return this.fiTaskV2HelperService.getUpdatedTaskParameter(parameter, loadArgs, {
            filterType: this.filterType,
            isArchived: false,
            taskStatuses: this.fiTaskV2HelperService.getTaskStatusFilterByViewMode(this.viewMode),
        });
    }

    private onTaskStatusChange(task: ITaskV2, newStatus: FiTaskStatusType): void {
        const prevStatus = task.Status;
        task.Status = newStatus;
        this.updateTaskStatus(task, newStatus).then((task) => {
            const index = this.items().findIndex((t) => t.Id === task.Id);
            if (index !== -1) {
                this.items[index] = task;
                this.cdr.markForCheck();
                if (this.fiTaskV2ListHelperService.shouldRemoveTaskFromList(this.viewMode, newStatus)) {
                    this.updateItems((items) => {
                        items.splice(index, 1);
                        return items;
                    });
                }

                if (this.fiTaskV2HelperService.canShowTaskSnackbarOnStatusChange(prevStatus, newStatus)) {
                    this.showTaskStatusChangeSnackbar(task, prevStatus, index);
                }
            }
        });
    }

    private updateTaskStatus(task: ITaskV2, newStatus: FiTaskStatusType, isUndo?: boolean): Promise<ITaskV2> {
        return new Promise<ITaskV2>((resolve, reject) => {
            this.fiTaskV2Service
                .updateTaskStatus([task], newStatus, isUndo)
                .then((newTasks) => resolve(newTasks[0]), reject);
        });
    }

    private showTaskStatusChangeSnackbar(task: ITaskV2, prevStatus: FiTaskStatusType, prevIndex: number): void {
        this.fiTaskV2HelperService.showTaskStatusChangeSnackbar(task.Name, task.Status).subscribe(async () => {
            const taskNew = await this.updateTaskStatus(task, prevStatus, true);
            this.fiTaskV2HelperService.showTaskStatusChangeUndoneSnackbar(taskNew.Name, prevStatus);

            // push item back to the list
            this.updateItems((items) => {
                items.splice(prevIndex, 0, taskNew);
                return items;
            });
            this.cdr.markForCheck();
        });
    }

    //# endregion

    private setDefaultSorting(sortColumn: FiTaskColumns): void {
        this.sortColumn = sortColumn;
        this.sortDirection = this.fiTaskV2ListHelperService.viewModeToDefaultSortDirection[this.viewMode];
    }
}
